import React, { useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { getWidgetTypeFromId } from '../components/ui/common/widgetMapping';
import { THEME_STORAGE_KEY } from '~/atoms/userPreferencesAtoms';

export interface PopoutWindowOptions {
  width?: number;
  height?: number;
  title?: string;
}

export interface PopoutWidgetData {
  type: string;
  props: Record<string, any>;
}

export const usePopoutWindow = () => {
  const popoutWindows = useRef<Map<string, Window>>(new Map());
  const popoutRoots = useRef<Map<string, ReturnType<typeof createRoot>>>(
    new Map(),
  );

  const openPopout = useCallback(
    (
      widgetId: string,
      widgetData: PopoutWidgetData,
      options: PopoutWindowOptions = {},
    ) => {
      const { width = 800, height = 600, title = 'Widget' } = options;

      // Close existing popout for this widget if it exists
      const existingWindow = popoutWindows.current.get(widgetId);
      if (existingWindow && !existingWindow.closed) {
        existingWindow.close();
      }

      // Open new window
      const popup = window.open(
        '',
        `widget-${widgetId}`,
        `width=${width},height=${height},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`,
      );

      if (!popup) {
        console.warn('Popup blocked or failed to open');
        return;
      }

      // Store reference
      popoutWindows.current.set(widgetId, popup);

      // Set up window
      popup.document.title = title;

      // Copy styles from main window
      const stylesheets = Array.from(document.styleSheets);
      stylesheets.forEach((stylesheet) => {
        try {
          if (stylesheet.href) {
            const link = popup.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = stylesheet.href;
            popup.document.head.appendChild(link);
          } else if (stylesheet.cssRules) {
            const style = popup.document.createElement('style');
            Array.from(stylesheet.cssRules).forEach((rule) => {
              style.appendChild(popup.document.createTextNode(rule.cssText));
            });
            popup.document.head.appendChild(style);
          }
        } catch (e) {
          // Cross-origin stylesheets may cause errors - ignore them
          console.warn('Could not copy stylesheet:', e);
        }
      });

      // Add custom tooltip implementation for popout windows
      // PrimeReact tooltips don't work correctly in popouts due to cross-window positioning issues
      const tooltipStyle = popup.document.createElement('style');
      tooltipStyle.textContent = `
        /* Hide PrimeReact tooltips in popout - they don't position correctly */
        .p-tooltip {
          display: none !important;
        }
        
        /* Ensure cards don't create stacking contexts that block tooltips */
        .p-card,
        .grid-stack-item-content,
        .react-grid-item {
          isolation: auto !important;
        }
        
        /* Custom tooltip element - must be at root level */
        .popout-tooltip {
          position: fixed !important;
          background-color: #2d2d2d !important;
          color: #ffffff !important;
          padding: 6px 10px !important;
          border-radius: 4px !important;
          font-size: 12px !important;
          white-space: nowrap !important;
          z-index: 2147483647 !important; /* Maximum z-index value */
          pointer-events: none !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
          display: none !important;
          font-family: Inter, sans-serif !important;
          margin: 0 !important;
          border: none !important;
          outline: none !important;
        }
        .popout-tooltip.visible {
          display: block !important;
        }
      `;
      popup.document.head.appendChild(tooltipStyle);

      // Create container and mount React
      const container = popup.document.createElement('div');
      container.id = 'popout-root';
      container.style.cssText =
        'height: 100vh; width: 100%; margin: 0; padding: 0;';
      popup.document.body.appendChild(container);

      // Reset body styles
      popup.document.body.style.cssText =
        'margin: 0; padding: 0; font-family: Inter, sans-serif;';

      // Ensure popout window has the current theme from main window
      // This ensures theme is synced when popout is created
      try {
        const currentTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (
          currentTheme &&
          (currentTheme === 'dark' || currentTheme === 'light')
        ) {
          popup.localStorage.setItem(THEME_STORAGE_KEY, currentTheme);
        }
      } catch (e) {
        // Ignore localStorage errors
      }

      const root = createRoot(container);
      popoutRoots.current.set(widgetId, root);

      // Setup tooltip system using MutationObserver to watch for elements
      const setupTooltips = () => {
        // Create tooltip element - MUST be appended directly to body, not inside any container
        let tooltipEl = popup.document.querySelector(
          '.popout-tooltip',
        ) as HTMLElement;
        if (!tooltipEl) {
          tooltipEl = popup.document.createElement('div');
          tooltipEl.className = 'popout-tooltip';
          // Ensure it's appended to body, not inside any widget container
          popup.document.body.appendChild(tooltipEl);
          // Force it to be at the root level
          tooltipEl.style.setProperty('position', 'fixed', 'important');
          tooltipEl.style.setProperty('z-index', '2147483647', 'important');
        }

        let showTimeout: ReturnType<typeof setTimeout> | null = null;
        let currentElement: HTMLElement | null = null;

        const showTooltip = (element: HTMLElement, text: string) => {
          if (showTimeout) {
            clearTimeout(showTimeout);
          }

          showTimeout = setTimeout(() => {
            const rect = element.getBoundingClientRect();
            tooltipEl.textContent = text;
            tooltipEl.style.display = 'block';

            // Force reflow
            void tooltipEl.offsetWidth;

            const tooltipRect = tooltipEl.getBoundingClientRect();
            let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
            let top = rect.top - tooltipRect.height - 8;

            // Keep within viewport
            if (left < 5) left = 5;
            if (left + tooltipRect.width > popup.innerWidth - 5) {
              left = popup.innerWidth - tooltipRect.width - 5;
            }
            if (top < 5) {
              top = rect.bottom + 8;
            }

            tooltipEl.style.left = left + 'px';
            tooltipEl.style.top = top + 'px';
            tooltipEl.classList.add('visible');
          }, 300);
        };

        const hideTooltip = () => {
          if (showTimeout) {
            clearTimeout(showTimeout);
            showTimeout = null;
          }
          tooltipEl.style.display = 'none';
          tooltipEl.classList.remove('visible');
          currentElement = null;
        };

        const attachTooltipHandlers = (element: HTMLElement) => {
          const tooltipText =
            element.getAttribute('data-pr-tooltip') ||
            element.getAttribute('title');
          if (!tooltipText) return;

          // Store original title
          if (
            element.hasAttribute('title') &&
            !element.hasAttribute('data-original-title')
          ) {
            element.setAttribute(
              'data-original-title',
              element.getAttribute('title') || '',
            );
          }

          const handleMouseEnter = () => {
            currentElement = element;
            showTooltip(element, tooltipText);
          };

          const handleMouseLeave = () => {
            if (currentElement === element) {
              hideTooltip();
              // Restore title
              if (element.hasAttribute('data-original-title')) {
                element.setAttribute(
                  'title',
                  element.getAttribute('data-original-title') || '',
                );
              }
            }
          };

          element.addEventListener('mouseenter', handleMouseEnter);
          element.addEventListener('mouseleave', handleMouseLeave);

          // Store handlers for cleanup
          (element as any).__tooltipHandlers = {
            handleMouseEnter,
            handleMouseLeave,
          };
        };

        // Attach to existing elements
        const attachToExisting = () => {
          const elements = popup.document.querySelectorAll(
            '[data-pr-tooltip], [title]',
          );
          elements.forEach((el) => {
            if (!(el as any).__tooltipHandlers) {
              attachTooltipHandlers(el as HTMLElement);
            }
          });
        };

        // Watch for new elements
        const observer = new MutationObserver(() => {
          attachToExisting();
        });

        observer.observe(popup.document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-pr-tooltip', 'title'],
        });

        // Initial attachment
        attachToExisting();
      };

      // Render the popout renderer component with widget data
      try {
        const PopoutRenderer = React.lazy(
          () => import('../components/ui/common/PopoutRenderer'),
        );

        const popoutElement = React.createElement(
          React.Suspense,
          {
            fallback: React.createElement(
              'div',
              {
                style: {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                  color: 'white',
                  backgroundColor: '#1a1a1a',
                },
              },
              'Loading...',
            ),
          },
          React.createElement(PopoutRenderer, { widgetData }),
        );

        root.render(popoutElement);

        // Setup tooltips after React renders (with a delay to ensure DOM is ready)
        setTimeout(setupTooltips, 500);
        // Also setup on next frame
        requestAnimationFrame(() => {
          setTimeout(setupTooltips, 100);
        });

        console.log('Popout rendered successfully with data:', widgetData);
      } catch (error: any) {
        console.error('Error rendering popout:', error);
        // Fallback: render error message
        const fallbackElement = popup.document.createElement('div');
        fallbackElement.innerHTML = `
        <div style="color: white; padding: 20px; font-family: Arial, sans-serif; background-color: #333; height: 100vh; display: flex; flex-direction: column; justify-content: center; align-items: center;">
          <h1>Popout Error</h1>
          <p>Error rendering widget: ${error?.message || 'Unknown error'}</p>
          <p>Widget Type: ${widgetData.type}</p>
        </div>
      `;
        container.appendChild(fallbackElement);
      }

      // Handle window close
      popup.addEventListener('beforeunload', () => {
        const root = popoutRoots.current.get(widgetId);
        if (root) {
          root.unmount();
          popoutRoots.current.delete(widgetId);
        }
        popoutWindows.current.delete(widgetId);
      });
    },
    [],
  );

  // Generic popout function that automatically creates widget data from component and props
  const openWidgetPopout = useCallback(
    (
      widgetComponent: React.ComponentType<any>,
      props: Record<string, any>,
      options: PopoutWindowOptions & { widgetId: string } = {
        widgetId: 'default',
      },
    ) => {
      const { widgetId, ...popoutOptions } = options;
      const widgetData: PopoutWidgetData = {
        type: getWidgetTypeFromId(widgetId),
        props,
      };

      openPopout(widgetId, widgetData, popoutOptions);
    },
    [openPopout],
  );

  const closePopout = useCallback((widgetId: string) => {
    const popup = popoutWindows.current.get(widgetId);
    if (popup && !popup.closed) {
      popup.close();
    }
  }, []);

  const isPopoutOpen = useCallback((widgetId: string): boolean => {
    const popup = popoutWindows.current.get(widgetId);
    return Boolean(popup && !popup.closed);
  }, []);

  return {
    openPopout,
    openWidgetPopout,
    closePopout,
    isPopoutOpen,
  };
};
